/**
 * Financial Viability — per-type preferences sub-form (D17, Phase 3.9).
 *
 * Pre-fix `PreferencesFormFor('financial')` fell through to the dispatcher's
 * default branch and rendered "No preferences form for type financial", so the
 * wizard AND the rerun panel showed an empty override editor for a type that
 * has four declared preferences.
 *
 * HURDLE-RATE UNIT (the reason this form is not a plain number box):
 * `FinancialPreferences.hurdleRateOverride` is a FRACTION — `reportContractsV2`
 * bounds it to [0, 1] — but `buildFinancialAnalysis` accepts either convention
 * (`raw <= 1.5 ? raw * 100 : raw`), so a user who read "hurdle rate" and typed
 * `1.2` meaning 1.2% got 120%. This form removes the ambiguity at the source:
 * the control is a PERCENT box with a `%` adornment, bounded 0-100, and it
 * writes the fraction. There is no entry that can mean two things.
 *
 * HONESTY: only `hurdleRateOverride` moves an engine number today. The other
 * three are recorded on the report and read by no engine, and each says so in
 * its own help text rather than implying a lever that does not exist.
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import type { FinancialPreferences } from '../../../types/siteRag';
import { FieldRow } from './shared';
import { validateFinancialPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateFinancialPreferences };

/** Contract bound (`reportContractsV2.financialPreferences`): fraction in [0,1]. */
const MAX_HURDLE_PCT = 100;

/** Fraction → percent for display, with float noise trimmed (0.07 ⇒ 7, not 7.000000000000001). */
function percentFromFraction(fraction: number | undefined): string {
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) return '';
  return String(Math.round(fraction * 1e6) / 1e4);
}

/** Percent → fraction for storage, clamped to the contract's [0,1] band. */
function fractionFromPercent(percent: number): number {
  const clamped = Math.min(MAX_HURDLE_PCT, Math.max(0, percent));
  return Math.round(clamped * 1e4) / 1e6;
}

interface Props {
  value: FinancialPreferences | undefined;
  onChange: (next: FinancialPreferences) => void;
}

export const FinancialPreferencesForm: React.FC<Props> = ({ value: rawValue, onChange }) => {
  const value: FinancialPreferences = (rawValue ?? {}) as FinancialPreferences;
  const set = <K extends keyof FinancialPreferences>(key: K, v: FinancialPreferences[K]) => {
    onChange({ ...value, [key]: v });
  };

  // The percent box keeps its own draft text so a partially-typed "12." is not
  // round-tripped through the fraction and swallowed mid-keystroke. The draft is
  // re-seeded whenever the stored fraction stops matching what it parses to.
  const storedPct = percentFromFraction(value.hurdleRateOverride);
  const [hurdleDraft, setHurdleDraft] = React.useState(storedPct);
  const lastStoredPct = React.useRef(storedPct);
  if (lastStoredPct.current !== storedPct) {
    lastStoredPct.current = storedPct;
    if (hurdleDraft.trim() !== '' && Number(hurdleDraft) !== Number(storedPct)) {
      setHurdleDraft(storedPct);
    }
  }

  const onHurdleChange = (raw: string) => {
    setHurdleDraft(raw);
    if (raw.trim() === '') {
      set('hurdleRateOverride', undefined);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    set('hurdleRateOverride', fractionFromPercent(parsed));
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FieldRow
          label="Hurdle / target IRR (%)"
          fieldKey="hurdleRateOverride"
          help={
            'The return the verdict bands project IRR against. Entered as a PERCENT '
            + '(12 = 12%) and stored as a fraction (0.12). Leave blank to band against '
            + 'the engine WACC / discount rate.'
          }
        >
          <div className="relative">
            <Input
              type="number" min={0} max={MAX_HURDLE_PCT} step={0.1}
              inputMode="decimal"
              placeholder="e.g. 12"
              aria-label="Hurdle or target IRR, percent"
              value={hurdleDraft}
              onChange={(e) => onHurdleChange(e.target.value)}
              className="h-9 text-sm pr-7"
            />
            <span
              className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-text-muted"
              aria-hidden="true"
            >
              %
            </span>
          </div>
          <div className="text-[10px] text-text-muted mt-1" data-testid="pref-hurdle-stored">
            {typeof value.hurdleRateOverride === 'number' && Number.isFinite(value.hurdleRateOverride)
              ? `Stored as ${value.hurdleRateOverride} (fraction). 0-100% only.`
              : 'Not set – the engine WACC is the hurdle.'}
          </div>
        </FieldRow>

        <FieldRow
          label="Pinned pathway comparison"
          fieldKey="pinnedPathwayComparisonId"
          help={
            'Comparison-document id this report was framed against. The engine loads '
            + 'the SITE pin (set in Financial Model → Pathway Comparison); this records '
            + 'the choice on the report and changes no figure on its own.'
          }
        >
          <Input
            type="text"
            placeholder="Comparison document id (optional)"
            value={value.pinnedPathwayComparisonId ?? ''}
            onChange={(e) => set('pinnedPathwayComparisonId', e.target.value.trim() === '' ? undefined : e.target.value)}
            className="h-9 text-sm"
          />
        </FieldRow>
      </div>

      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex items-center justify-between" data-testid="pref-field-includeTaxCredits">
          <div>
            <Label className="type-section text-text-secondary">Include IRA tax credits (ITC/PTC)</Label>
            <div className="text-[10px] text-text-muted">
              Presentation flag only. The engine models credits from the financial model&apos;s own
              declared ITC / PTC fields, so this toggle moves no NPV, IRR or LCOE figure.
            </div>
          </div>
          <Switch
            checked={value.includeTaxCredits !== false}
            onCheckedChange={(b) => set('includeTaxCredits', b)}
          />
        </div>

        <div className="flex items-center justify-between" data-testid="pref-field-includePpaAdjustment">
          <div>
            <Label className="type-section text-text-secondary">Include PPA revenue adjustment</Label>
            <div className="text-[10px] text-text-muted">
              Presentation flag only. PPA revenue is always modelled from the financial model&apos;s
              contracted price, term and escalator, with a merchant tail after the term.
            </div>
          </div>
          <Switch
            checked={!!value.includePpaAdjustment}
            onCheckedChange={(b) => set('includePpaAdjustment', b)}
          />
        </div>
      </div>
    </div>
  );
};
